import { isClosedProgressType } from '../constants'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

export type StatusTaskGroup = {
  status: StatusDocument
  tasks: Array<TaskDocument>
  isClosed: boolean
}

/**
 * The order status groups appear in a grouped list (design 1c / 6a): active
 * work first, then what's queued, then closed work at the bottom.
 */
const GROUP_ORDER: Record<string, number> = { IP: 0, PL: 1, BL: 2, CD: 3, CL: 4 }

/**
 * Split tasks into one group per status, in display order. Task order inside a
 * group is preserved. Empty groups are dropped; tasks whose status isn't known
 * are left out (they have nowhere meaningful to go).
 */
export function groupTasksByStatus(
  tasks: ReadonlyArray<TaskDocument>,
  statuses: ReadonlyArray<StatusDocument>,
): Array<StatusTaskGroup> {
  const byStatus = new Map<string, Array<TaskDocument>>()
  for (const task of tasks) {
    const bucket = byStatus.get(task.statusId)
    if (bucket) bucket.push(task)
    else byStatus.set(task.statusId, [task])
  }

  return statuses
    .map((status, index) => ({ status, index }))
    .filter(({ status }) => (byStatus.get(status.id)?.length ?? 0) > 0)
    .sort((a, b) => {
      const byType =
        (GROUP_ORDER[a.status.progressType] ?? 99) - (GROUP_ORDER[b.status.progressType] ?? 99)
      return byType !== 0 ? byType : a.index - b.index
    })
    .map(({ status }) => ({
      status,
      tasks: byStatus.get(status.id) ?? [],
      isClosed: isClosedProgressType(status.progressType),
    }))
}
